/**
 * useTextSelection — captures text selection inside a ref'd container.
 * Returns { selection, clearSelection } where selection = { text, rect, range } | null.
 * Fires onSelect(text, rect) whenever a non-empty selection is committed.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

export default function useTextSelection(containerRef, { onSelect } = {}) {
  const [selection, setSelection] = useState(null);
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  const handleMouseUp = useCallback((event) => {
    // Clicking the highlight popover collapses the browser selection before
    // the button's click handler runs — ignore those events so the popover
    // stays mounted long enough to save.
    const target = event?.target;
    if (target && typeof target.closest === 'function'
        && target.closest('[data-highlight-popover]')) {
      return;
    }

    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.toString().trim()) {
      setSelection(null);
      return;
    }
    if (containerRef.current && !containerRef.current.contains(sel.anchorNode)) {
      setSelection(null);
      return;
    }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const text = sel.toString().trim();
    const result = { text, rect, range: range.cloneRange() };
    setSelection(result);
    if (onSelectRef.current) onSelectRef.current(text, rect);
  }, [containerRef]);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchend', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [handleMouseUp]);

  const clearSelection = useCallback(() => {
    window.getSelection()?.removeAllRanges();
    setSelection(null);
  }, []);

  return { selection, clearSelection };
}
